import { createTheme } from '@mui/material/styles'
import { ptBR as gridBR } from '@mui/x-data-grid/locales'
import { ptBR as pickerBR } from '@mui/x-date-pickers/locales'
import { ptBR as localeBR } from '@mui/material/locale'
import { darkTokens as d, lightTokens as l } from './tokens'

declare module '@mui/material/styles' {
  interface Theme {
    customShadows: { shadowBox: string }
    tokens: typeof d | typeof l
  }
  interface ThemeOptions {
    customShadows?: { shadowBox?: string }
    tokens?: typeof d | typeof l
  }
}

const typography = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontSize: 14,
  h1: { fontWeight: 700, fontSize: '2rem',    letterSpacing: '-0.3px', lineHeight: 1.15 },
  h2: { fontWeight: 700, fontSize: '1.5rem',  letterSpacing: '-0.3px', lineHeight: 1.2  },
  h3: { fontWeight: 600, fontSize: '1.125rem',letterSpacing: '-0.2px' },
  h4: { fontWeight: 600, fontSize: '1rem',    letterSpacing: '-0.1px' },
  h5: { fontWeight: 600, fontSize: '0.875rem' },
  h6: { fontWeight: 500, fontSize: '0.8125rem' },
  body1:    { fontSize: '0.9375rem', lineHeight: 1.5, letterSpacing: '-0.003em' },
  body2:    { fontSize: '0.875rem',  lineHeight: 1.5 },
  subtitle1:{ fontWeight: 500, fontSize: '0.875rem' },
  caption:  { fontSize: '0.75rem',   fontWeight: 400, lineHeight: 1.4 },
  button:   { fontWeight: 600, fontSize: '0.875rem', textTransform: 'none' as const },
}

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary:    { main: d.teal,    dark: d.tealDark, light: '#2dd4bf', contrastText: '#fff' },
    secondary:  { main: d.teal,    contrastText: '#fff' },
    success:    { main: d.teal,    dark: d.tealDark,  contrastText: '#fff' },
    error:      { main: '#f87171', dark: '#ef4444' },
    warning:    { main: '#f59e0b' },
    background: { default: d.bgApp, paper: d.bgCard },
    text:       { primary: d.textPrimary, secondary: d.textSecondary, disabled: d.textMuted },
    divider:    d.border,
  },
  typography,
  shape: { borderRadius: d.radiusSm },
  tokens: d,
  customShadows: {
    shadowBox: `0 4px 16px rgba(0,0,0,0.4), 0 0 0 1px ${d.borderCard}`,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: d.radiusSm, fontWeight: 600, textTransform: 'none', boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: { '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none', backgroundColor: d.bgCard, border: `1px solid ${d.borderCard}` },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { backgroundColor: d.bgCard, border: `1px solid ${d.borderCard}`, borderRadius: d.radiusMd, boxShadow: 'none' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: d.bgInput, borderRadius: d.radiusSm,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: d.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: d.borderCard },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: d.teal, borderWidth: '1.5px' },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 9999, fontWeight: 500, fontSize: '0.75rem' } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { borderRadius: 6, fontSize: '0.75rem', backgroundColor: '#0a1015', border: `1px solid ${d.borderCard}` },
      },
    },
    MuiMenuItem: {
      styleOverrides: { root: { borderRadius: 6, fontSize: '0.875rem', margin: '1px 4px' } },
    },
  },
}, gridBR, pickerBR, localeBR)

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary:    { main: l.teal,     dark: '#0a7a70', light: l.tealLight, contrastText: '#fff' },
    secondary:  { main: l.teal,     contrastText: '#fff' },
    success:    { main: l.teal,     dark: '#0a7a70', contrastText: '#fff' },
    error:      { main: '#ef4444',  dark: '#dc2626' },
    warning:    { main: '#f59e0b' },
    background: { default: l.bgBody, paper: l.bgCard },
    text:       { primary: l.textPrimary, secondary: l.textSecondary, disabled: l.textMuted },
    divider:    l.border,
  },
  typography,
  shape: { borderRadius: l.radiusSm },
  tokens: l,
  customShadows: {
    shadowBox: `0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px ${l.borderCard}`,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: l.radiusSm, fontWeight: 600, textTransform: 'none', boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: { '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none', backgroundColor: l.bgCard, border: `1px solid ${l.borderCard}`, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { backgroundColor: l.bgCard, border: `1px solid ${l.borderCard}`, borderRadius: l.radiusMd, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: l.bgInput, borderRadius: l.radiusSm,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: l.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: l.borderCard },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: l.teal, borderWidth: '1.5px' },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 9999, fontWeight: 500, fontSize: '0.75rem' } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { borderRadius: 6, fontSize: '0.75rem', backgroundColor: '#1a2a35', color: '#e8f0f2', border: `1px solid ${l.borderCard}` },
      },
    },
    MuiMenuItem: {
      styleOverrides: { root: { borderRadius: 6, fontSize: '0.875rem', margin: '1px 4px' } },
    },
  },
}, gridBR, pickerBR, localeBR)
